<script lang="ts" setup>
import ExpandTransition from '@shared/animation/transitions/ExpandTransition/ExpandTransition.vue';
import VStatusTag, { type VStatusTagProps } from '@shared/component/VStatusTag/VStatusTag.vue';
import VStyledHtml from '@shared/component/VStyledHtml/VStyledHtml.vue';
import VTooltip from '@shared/component/VTooltip/VTooltip.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useId } from '@tools/composables/id';
import { type MaybeRef } from '@vueuse/core';
import { computed, unref } from 'vue';

export interface Props {
  label?: string;
  labelFor?: string;
  labelSize?: 1 | 2 | 3;
  labelTextColor?: keyof typeof labelTextColorClasses;
  htmlLabel?: string;
  badge?: { text: string; color: VStatusTagProps['color'] };
  tooltip?: string;
  errors?: MaybeRef<MaybeRef<string | undefined>[]>;
  required?: boolean;
  labelOnOwnRow?: boolean;
  forceLabelTextAlignLeft?: boolean;
  labelInBold?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  labelSize: 2,
  labelOnOwnRow: false,
  labelTextColor: '',
  forceLabelTextAlignLeft: true,
  labelInBold: true,
});

const slots = defineSlots<{
  default(): any;
  footer(): any;
  subLabel(): any;
}>();

const ids = useId('field-row', ['label']);

const sanitizedErrors = computed(() => unref(props.errors)?.filter((x) => !!x));
const showLabel = computed(() => !(props.label === undefined && props.htmlLabel === undefined));
const hasLabel = computed(() => props.label?.length || props.htmlLabel?.length);

const labelSizeClasses = {
  1: 'flex-1',
  2: 'flex-[2]',
  3: 'flex-[3]',
};

const labelTextColorClasses = {
  '': 'text-black',
  primary900: 'text-primary-900',
} as const;
</script>

<template>
  <div
    :aria-labelledby="props.labelFor ? `${props.labelFor}-label` : ids.label"
    class="flex w-full flex-col flex-nowrap items-start"
    :class="[
      { error: sanitizedErrors && sanitizedErrors.length > 0 },
      { 'sm:flex-row sm:gap-12': !labelOnOwnRow },
    ]"
    data-field-row
    :data-field--has-error="!!sanitizedErrors && sanitizedErrors.length > 0"
    role="listitem"
  >
    <div
      v-if="showLabel"
      class="relative flex select-none text-black em:mb-1.5 sm:mb-0 sm:min-h-form-row"
      :class="[
        labelSizeClasses[props.labelSize],
        labelTextColorClasses[props.labelTextColor],
        { 'sm:justify-end sm:text-end': !props.forceLabelTextAlignLeft && !props.labelOnOwnRow },
        $slots.subLabel ? 'flex-col items-start' : 'items-center',
      ]"
    >
      <label
        class="mb-0 overflow-wrap-anywhere sm:ml-0 sm:mr-0 sm:mt-0"
        :class="{
          flex: hasLabel,
          'font-bold': props.labelInBold,
          'hidden sm:flex': !hasLabel,
        }"
        :id="props.labelFor ? `${props.labelFor}-label` : ids.label"
        :for="props.labelFor"
      >
        <span
          v-if="label || htmlLabel"
          v-html="label ?? htmlLabel"
          :class="{
            'whitespace-pre-wrap': !props.htmlLabel,
            'sm:text-right': !label,
            [`after:ml-1 after:text-red after:content-['*']`]: props.required,
          }"
        >
        </span>
        <VStatusTag v-if="badge" :color="badge.color" class="ml-2">{{ badge.text }}</VStatusTag>

        <span v-if="tooltip" class="ml-4">
          <VTooltip>
            <FontAwesomeIcon aria-label="Information" icon="fa-regular fa-info-circle" />
            <template #content><span v-html="tooltip"></span></template>
          </VTooltip>
        </span>
      </label>

      <VStyledHtml v-if="$slots.subLabel" class="mt-2 text-sm text-gray-400">
        <slot name="subLabel"></slot>
      </VStyledHtml>
    </div>
    <div class="relative flex w-full min-w-0 flex-[3] flex-col self-center">
      <div class="flex flex-wrap items-center gap-2">
        <slot></slot>
      </div>

      <ExpandTransition>
        <span
          v-if="(sanitizedErrors?.length ?? 0) > 0"
          v-sanitized-html="sanitizedErrors?.join(', ')"
          class="text-sm text-red !duration-400"
          role="status"
        ></span>
      </ExpandTransition>

      <div v-if="slots.footer" class="mt-4">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>
